import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { joinCase } from '@/lib/ws/socket';
import { useConnectionStore } from '@/state/connection.store';
import { cancelTrip, dispatchTrips, fetchCaseTrips } from './api';
import { caseTripsKey, mergeTrip } from './trips';
import type { DispatchTripsInput, TripView } from './types';

// Live ground-transport trips for a case (feat/transport slice T5).
//
// ONE HTTP read to seed, then the WS owns freshness: `TripChanged@v1` →
// `applyEvent` → `setQueryData(['trips', caseUrn])`. Nothing in this feature is
// driven by a timer or a polling interval — a polled "live" surface is forbidden
// by frontend-conventions §15 / brief §6.8. The only extra read that ever fires
// is the single reconnect catch-up below, which is an EVENT (socket 'connect'),
// not a clock.

/** Fold a trip the operator's own write returned straight into the cache. */
function writeTrip(qc: QueryClient, trip: TripView): void {
  qc.setQueryData<TripView[]>(caseTripsKey(trip.caseUrn), (prev) => mergeTrip(prev, trip));
}

/**
 * The case's trips, live.
 *
 * `staleTime: Infinity` + `refetchOnWindowFocus: false` because the socket is the
 * refresh mechanism; letting TanStack re-fetch on its own would race the WS merge
 * and, in a hub closure with a drawer open per operator, hammer the BFF.
 *
 * WS-DROP HANDLING. A dropped socket loses two things: the pushes themselves and
 * the room membership that would deliver the next ones. socket.io reconnects on
 * its own and the connection store flips `connected` back to true — we hang the
 * recovery off THAT transition: re-`joinCase` (rooms do not survive a reconnect,
 * and `join:case` is idempotent) plus exactly ONE `refetch()` to pick up whatever
 * changed while we were deaf. One event, one read.
 *
 * `joinCase` on mount is deliberately NOT paired with a `leaveCase` on unmount:
 * this card lives inside a Radix tab that unmounts when the operator switches to
 * "Activity", and leaving the room there would silently kill the drawer's OWN
 * live reservations feed. The drawer owns the join/leave lifecycle for the case
 * (`roster-pnr-drawer.tsx`); this is a redundant, idempotent join so the hook is
 * still correct if it is ever mounted somewhere that does not join.
 */
export function useCaseTrips(caseUrn: string | undefined, enabled = true) {
  const active = enabled && !!caseUrn;

  const query = useQuery<TripView[]>({
    queryKey: caseTripsKey(caseUrn ?? ''),
    queryFn: () => fetchCaseTrips(caseUrn as string),
    enabled: active,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!active || !caseUrn) return;
    joinCase(caseUrn);
  }, [active, caseUrn]);

  const connected = useConnectionStore((s) => s.connected);
  const wasConnected = useRef(connected);
  const { refetch } = query;
  useEffect(() => {
    const reconnected = connected && !wasConnected.current;
    wasConnected.current = connected;
    if (!reconnected || !active || !caseUrn) return;
    joinCase(caseUrn);
    void refetch();
  }, [connected, active, caseUrn, refetch]);

  return query;
}

/**
 * MANUAL dispatch (brief §5, T5). 202 — the returned trips are REQUESTED; the
 * vendor assignment arrives later as a `TripChanged` push. We still fold the 202
 * bodies into the cache so the row appears the instant the operator confirms,
 * instead of waiting on a round trip through Kafka. The later push carries the
 * whole `TripView` and replaces it — so the write is idempotent either way.
 */
export function useDispatchTrips() {
  const qc = useQueryClient();
  return useMutation<TripView[], Error, DispatchTripsInput>({
    mutationFn: (input) => dispatchTrips(input),
    onSuccess: (trips) => {
      for (const trip of trips) writeTrip(qc, trip);
    },
  });
}

export interface CancelTripInput {
  urn: string;
  reason: string;
}

/** Cancel a non-terminal trip. Same fold-the-response-in rule as dispatch. */
export function useCancelTrip() {
  const qc = useQueryClient();
  return useMutation<TripView, Error, CancelTripInput>({
    mutationFn: ({ urn, reason }) => cancelTrip(urn, reason),
    onSuccess: (trip) => writeTrip(qc, trip),
  });
}
